import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import ActionLoader from '../../components/ActionLoader';
import { useNavigation, useRoute } from '@react-navigation/native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import { getThemeColors } from '../../utils/themeStyles';
import { useAuth } from '../../context/AuthContext';
import { orgApi } from '../../api/org';
import { getPendingInvite, setPendingInvite, clearPendingInvite } from '../../navigation/pendingInvite';
import { useT } from '../../i18n/useT';

interface InviteInfo { company_name: string; status: string; expired: boolean; matches_me: boolean }

// Invitation confirmation screen (deep-link /invite/:token).
// - Signed out: explains the invitation + a sign-in / sign-up call to action.
// - Signed in: shows the REAL state (to join / already joined / other address / expired).
export default function InviteScreen() {
  const t = useT();
  const nav = useNavigation<any>();
  const route = useRoute<any>();
  const { isDarkTheme } = useTheme();
  const c = getThemeColors(isDarkTheme);
  const { user, getAccessToken, logout } = useAuth();
  const raw = route?.params?.token;
  const token: string | null =
    raw && raw !== 'undefined' && raw !== 'null' ? raw : getPendingInvite();

  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<'accepted' | 'declined' | null>(null);

  // Remember the token so it survives authentication (native + Entra).
  useEffect(() => { if (token) setPendingInvite(token); }, [token]);

  // Signed in: the invitation's real state, whatever its status.
  const load = useCallback(async () => {
    if (!user || !token) return;
    setLoading(true); setMsg(null);
    try {
      setInfo(await orgApi.invitationByToken(getAccessToken(), token));
    } catch {
      setInfo(null);
      setMsg(t('invitation.notFoundOrExpired'));
    } finally { setLoading(false); }
  }, [user, token, getAccessToken, t]);
  useEffect(() => { void load(); }, [load]);

  const accept = async () => {
    if (!token) return;
    setLoading(true); setMsg(null);
    try {
      await orgApi.acceptInvitation(getAccessToken(), token);
      clearPendingInvite();
      setOutcome('accepted');
    } catch {
      await load(); // reload the real state (may already be accepted / wrong account)
      setMsg(t('invitation.acceptFailed'));
    } finally { setLoading(false); }
  };

  const decline = async () => {
    if (!token) return;
    setLoading(true); setMsg(null);
    try {
      await orgApi.declineInvitation(getAccessToken(), token);
      clearPendingInvite();
      setOutcome('declined');
    } catch {
      await load();
      setMsg(t('invitation.declineFailed'));
    } finally { setLoading(false); }
  };

  const goSignIn = () => {
    try {
      if (token && Platform.OS === 'web' && typeof window !== 'undefined') {
        window.sessionStorage.setItem('ccjaw.returnTo', `/invite/${token}`);
      }
    } catch { /* ignore */ }
    nav.navigate('Login');
  };
  const goHome = () => { clearPendingInvite(); nav.navigate(user ? 'Main' : 'PublicMissions'); };
  const switchAccount = () => { logout(); }; // clears the session -> sign back in with the right address

  // Picks the content shown to a signed-in visitor.
  const renderAuthed = () => {
    if (loading) return <View style={{ marginVertical: 20 }}><ActionLoader label={t('invitation.loading')} /></View>;
    if (outcome === 'accepted') return (
      <Result c={c} color={c.success}
        text={t('invitation.joined', { name: info?.company_name ?? '' })}
        btn={t('invitation.continue')} onPress={goHome} />
    );
    if (outcome === 'declined') return (
      <Result c={c} text={t('invitation.declined', { name: info?.company_name ?? '' })}
        btn={t('invitation.goHome')} onPress={goHome} />
    );
    if (!info) return <Result c={c} text={msg ?? t('invitation.notFound')} btn={t('invitation.goHome')} onPress={goHome} />;
    if (info.status === 'accepted' && info.matches_me) return (
      <Result c={c} color={c.success} text={t('invitation.alreadyJoined', { name: info.company_name })}
        btn={t('invitation.continue')} onPress={goHome} />
    );
    if (!info.matches_me) return (
      <View>
        <Text style={[st.body, { color: c.textSecondary }]}>{t('invitation.otherAddress')}</Text>
        <Btn c={c} label={t('invitation.switchAccount')} onPress={switchAccount} />
        <TouchableOpacity onPress={goHome} style={{ marginTop: 12 }}>
          <Text style={{ color: c.textTertiary, textAlign: 'center' }}>{t('invitation.goHome')}</Text>
        </TouchableOpacity>
      </View>
    );
    if (info.expired || info.status === 'cancelled') return (
      <Result c={c} text={t('invitation.expiredOrCancelled')} btn={t('invitation.goHome')} onPress={goHome} />
    );
    // pending + matches_me
    return (
      <View>
        <Text style={[st.body, { color: c.textSecondary }]}>
          {t('invitation.invitedTo')}
          <Text style={{ fontWeight: '700', color: c.textPrimary }}>{info.company_name}</Text>.
        </Text>
        <Btn c={c} label={t('invitation.joinCompany', { name: info.company_name })} onPress={accept} />
        <TouchableOpacity onPress={decline} style={[st.secondary, { borderColor: c.border }]}>
          <Text style={{ color: c.textSecondary, textAlign: 'center', fontWeight: '600' }}>{t('invitation.declineInvitation')}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={goHome} style={{ marginTop: 12 }}>
          <Text style={{ color: c.textTertiary, textAlign: 'center' }}>{t('invitation.backHome')}</Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <SafeAreaView style={[st.c, { backgroundColor: c.background }]}>
      <View style={[st.card, { backgroundColor: c.cardBackground }]}>
        <Ionicons name="mail-open-outline" size={40} color={c.primary} style={{ alignSelf: 'center', marginBottom: 8 }} />
        <Text style={[st.title, { color: c.textPrimary }]}>{t('invitation.title')}</Text>

        {!token ? (
          <Result c={c} text={t('invitation.badLink')} btn={t('invitation.goHome')} onPress={goHome} />
        ) : !user ? (
          <View>
            <Text style={[st.body, { color: c.textSecondary }]}>{t('invitation.signInPrompt')}</Text>
            <Btn c={c} label={t('invitation.signIn')} onPress={goSignIn} />
          </View>
        ) : renderAuthed()}
      </View>
    </SafeAreaView>
  );
}

function Btn({ c, label, onPress }: any) {
  return (
    <TouchableOpacity style={[st.btn, { backgroundColor: c.primary }]} onPress={onPress} activeOpacity={0.85}>
      <Text style={st.btnT}>{label}</Text>
    </TouchableOpacity>
  );
}

function Result({ c, color, text, btn, onPress }: any) {
  return (
    <View>
      <Text style={[st.body, { color: color ?? c.textSecondary }]}>{text}</Text>
      <Btn c={c} label={btn} onPress={onPress} />
    </View>
  );
}

const st = StyleSheet.create({
  c: { flex: 1, justifyContent: 'center', padding: 20 },
  card: { borderRadius: 16, padding: 24, maxWidth: 440, width: '100%', alignSelf: 'center' },
  title: { fontSize: 20, fontWeight: '700', textAlign: 'center', marginBottom: 12 },
  body: { fontSize: 15, lineHeight: 22, textAlign: 'center', marginBottom: 20 },
  btn: { borderRadius: 10, paddingVertical: 14, alignItems: 'center' },
  btnT: { color: '#FFF', fontSize: 16, fontWeight: '700' },
  secondary: { borderRadius: 10, paddingVertical: 12, alignItems: 'center', borderWidth: 1, marginTop: 10 },
});
